import React from "react";
import { Auth0Provider as Auth0ProviderSDK } from "@auth0/auth0-react";

interface Auth0ProviderProps {
  children: React.ReactNode;
}

/**
 * 🏢 MULTI-TENANT SAAS: Auth0 Configuration
 *
 * IMPORTANT: For refresh tokens to work, the following must be configured
 * in Auth0 Dashboard (https://manage.auth0.com):
 *
 * 1. Application Settings:
 *    - Application Type: Single Page Application
 *    - Token Endpoint Auth Method: None (for public SPA)
 *
 * 2. API Settings (for your API, e.g., "app.forcemx.io API"):
 *    - Enable "Allow Offline Access" ✅ CRITICAL
 *    - Token Endpoint Auth: None
 *    - Grant Types: Authorization Code, Refresh Token ✅ CRITICAL
 *
 * 3. Application API Settings:
 *    - Authorized APIs: Add your API with "offline_access" scope
 *
 * WITHOUT THESE SETTINGS:
 * - Refresh tokens will NOT be issued
 * - Users will see "Missing Refresh Token" errors
 * - Silent token renewal will fail
 */
export const Auth0Provider: React.FC<Auth0ProviderProps> = ({ children }) => {
  // Read from root .env file (Vite requires VITE_ prefix for browser builds)
  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const audience = import.meta.env.VITE_AUTH0_API_AUDIENCE;

  if (!domain || !clientId) {
    console.error(
      "❌ Auth0 configuration missing. Please set VITE_AUTH0_DOMAIN and VITE_AUTH0_CLIENT_ID in root .env file.",
    );
    return (
      <div className="auth-error">
        <h2>Authentication Configuration Error</h2>
        <p>Please configure Auth0 credentials in root .env file.</p>
      </div>
    );
  }

  return (
    <Auth0ProviderSDK
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: audience || "https://api.app.forcemx.io",
        scope: "openid profile email offline_access"  // offline_access = refresh tokens
      }}
      useRefreshTokens={true}  // Enable automatic token refresh
      cacheLocation="localstorage"  // Persist across page reloads
      onRedirectCallback={(appState) => {
        // Return to the page the user was on before an expired-session re-login.
        // popstate lets the already-mounted BrowserRouter pick up the new URL.
        const returnTo = appState?.returnTo || window.location.pathname;
        window.history.replaceState({}, document.title, returnTo);
        window.dispatchEvent(new PopStateEvent("popstate"));
      }}
    >
      {children}
    </Auth0ProviderSDK>
  );
};
